import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw, convertFromRaw } from "draft-js";
import axios from "axios";
import moment from "moment";
import draftToHtml from "draftjs-to-html";
import ImageUpload from "./ImageUpload";
import {
	Container,
	Row,
	Col,
	Form,
	Button,
	Card,
	Alert,
	Spinner
} from "react-bootstrap";

function AdminArticleForm({ user }) {
	const { id } = useParams(); // detect /edit/:id
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [categories, setCategories] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const formRef = React.useRef(null);

	const [editorState, setEditorState] = useState(EditorState.createEmpty());
	const [formData, setFormData] = useState({
		title: "",
		slug: "",
		category: "",
		tags: [],
		thumbnail: "",
		status: "draft",
		featured: false,
		article_type: "article",
		publish_date: "",
		meta_title: "",
		meta_desc: ""
	});

	useEffect(() => {
		fetchCategories();
		if (id) fetchArticle();
	}, [id]);

	const fetchCategories = async () => {
		try {
			const res = await axios.get("/admin/articles/categories", {
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (res.data.status && res.data.data) {
				setCategories(res.data.data);
			}
		} catch (e) {
			console.error("Category fetch failed", e);
		}
	};

	const fetchArticle = async () => {
		setLoading(true);
		try {
			const res = await axios.get(`/admin/articles/${id}`, {
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (res.data.status) {
				const a = res.data.data;
				setFormData({
					title: a.title || "",
					slug: a.slug || "",
					category: a.category || "",
					tags: a.tags ? JSON.parse(a.tags) : [],
					thumbnail: a.thumbnail || "",
					status: a.status || "draft",
					featured: a.featured || false,
					article_type: a.article_type || "article",
					publish_date: a.publish_date ? moment(a.publish_date).format("YYYY-MM-DDTHH:mm") : "",
					meta_title: a.meta_title || "",
					meta_desc: a.meta_desc || ""
				});

				if (a.content) {
					try {
						const contentState = convertFromRaw(JSON.parse(a.content));
						setEditorState(EditorState.createWithContent(contentState));
					} catch {
						setEditorState(EditorState.createWithContent(ContentState.createFromText(a.content)));
					}
				}
			}
		} catch (err) {
			setError("Failed to load article");
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((p) => ({ ...p, [name]: value }));
		// Clear validation error for this field
		if (validationErrors[name]) {
			setValidationErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	const handleTagsChange = (e) => {
		const tags = e.target.value.split(",").map((t) => t.trim()).filter(Boolean);
		setFormData((p) => ({ ...p, tags }));
	};

	const handleImageUpload = (url) => {
		setFormData((p) => ({ ...p, thumbnail: url }));
	};

	const scrollToTop = () => {
		if (formRef.current) {
			formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	const validateForm = () => {
		const errors = {};
		
		if (!formData.title.trim()) {
			errors.title = "Title is required";
		} else if (formData.title.length < 3) {
			errors.title = "Title must be at least 3 characters";
		} else if (formData.title.length > 255) {
			errors.title = "Title must not exceed 255 characters";
		}
		
		if (!formData.category) {
			errors.category = "Sports category is required";
		}
		
		const contentText = editorState.getCurrentContent().getPlainText();
		if (!contentText.trim()) {
			errors.content = "Content is required";
		} else if (contentText.length < 10) {
			errors.content = "Content must be at least 10 characters";
		}
		
		if (formData.slug && formData.slug.length > 255) {
			errors.slug = "Slug must not exceed 255 characters";
		}
		
		if (formData.meta_title && formData.meta_title.length > 255) {
			errors.meta_title = "Meta title must not exceed 255 characters";
		}
		
		if (formData.meta_desc && formData.meta_desc.length > 500) {
			errors.meta_desc = "Meta description must not exceed 500 characters";
		}
		
		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");
		
		if (!validateForm()) {
			setError("Please fix the validation errors before submitting");
			scrollToTop();
			return;
		}
		
		setSaving(true);
		try {
			const rawContent = convertToRaw(editorState.getCurrentContent());
			const payload = {
				...formData,
				content: JSON.stringify(rawContent),
				publish_date: formData.publish_date || null
			};
			
			// Remove slug if empty (will be auto-generated on backend)
			if (!payload.slug || !payload.slug.trim()) {
				delete payload.slug;
			}

			const headers = { Authorization: `Bearer ${user.token}` };
			let res;
			if (id)
				res = await axios.put(`/admin/articles/${id}`, payload, { headers });
			else
				res = await axios.post("/admin/articles", payload, { headers });

			if (res.data.status) {
				setSuccess(res.data.message || (id ? "Article updated successfully!" : "Article created successfully!"));
				setTimeout(() => navigate("/admin-panel/articles"), 1500);
			} else {
				setError(res.data.message || "Failed to save article");
			}
		} catch (err) {
			const errorMessage = err.response?.data?.message || err.message || "Failed to save article. Please try again.";
			setError(errorMessage);
			scrollToTop();
			console.error("Error saving article:", err);
		} finally {
			setSaving(false);
		}
	};

	if (loading)
		return (
			<div id='fullHeight'>
				<div id='loading' />
			</div>
		);

	return (
		<Container className="py-4" ref={formRef}>
			<Card>
				<Card.Header>
					<h4 className="mb-0">{id ? "Edit Article" : "Create New Article"}</h4>
				</Card.Header>
				<Card.Body>
					{error && (
						<Alert variant="danger" onClose={() => setError("")} dismissible>
							{error}
						</Alert>
					)}
					{success && (
						<Alert variant="success" onClose={() => setSuccess("")} dismissible>
							{success}
						</Alert>
					)}

					<Form onSubmit={handleSubmit}>
						{/* Basic Information Section */}
						<h5 className="mb-3 pb-2 border-bottom">📝 Basic Information</h5>
						<Row className="g-3 mb-4">
							<Col xs={12}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Title *</Form.Label>
									<Form.Control
										name="title"
										value={formData.title}
										onChange={handleChange}
										isInvalid={!!validationErrors.title}
										placeholder="Enter article title"
									/>
									<Form.Control.Feedback type="invalid">
										{validationErrors.title}
									</Form.Control.Feedback>
								</Form.Group>
							</Col>

							<Col md={6}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Slug</Form.Label>
									<Form.Control
										name="slug"
										value={formData.slug}
										onChange={handleChange}
										isInvalid={!!validationErrors.slug}
										placeholder="Auto-generated if empty"
									/>
									<Form.Control.Feedback type="invalid">
										{validationErrors.slug}
									</Form.Control.Feedback>
									<Form.Text className="text-muted">
										URL-friendly version of the title
									</Form.Text>
								</Form.Group>
							</Col>

							<Col md={6}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Sports Category *</Form.Label>
									<Form.Select
										name="category"
										value={formData.category}
										onChange={handleChange}
										isInvalid={!!validationErrors.category}
									>
										<option value="">Select Sports</option>
										{categories.map((c) => (
											<option key={c} value={c}>{c}</option>
										))}
									</Form.Select>
									<Form.Control.Feedback type="invalid">
										{validationErrors.category}
									</Form.Control.Feedback>
								</Form.Group>
							</Col>

							<Col md={6}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Tags</Form.Label>
									<Form.Control
										value={formData.tags.join(", ")}
										onChange={handleTagsChange}
										placeholder="e.g. NFL, Injuries, Analysis"
									/>
									<Form.Text className="text-muted">
										Separate tags with commas
									</Form.Text>
								</Form.Group>
							</Col>

							<Col md={6}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Publish Date</Form.Label>
									<Form.Control
										type="datetime-local"
										name="publish_date"
										value={formData.publish_date}
										onChange={handleChange}
									/>
									<Form.Text className="text-muted">
										Schedule when this article will be published
									</Form.Text>
								</Form.Group>
							</Col>
						</Row>

						{/* Publishing Options Section */}
						<h5 className="mb-3 pb-2 border-bottom">⚙️ Publishing Options</h5>
						<Row className="g-3 mb-4">
							<Col md={4}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Status</Form.Label>
									<Form.Select
										name="status"
										value={formData.status}
										onChange={handleChange}
									>
										<option value="draft">📝 Draft</option>
										<option value="published">✅ Published</option>
									</Form.Select>
								</Form.Group>
							</Col>

							<Col md={4}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Content Type</Form.Label>
									<Form.Select
										name="article_type"
										value={formData.article_type}
										onChange={handleChange}
									>
										<option value="article">📰 Article</option>
										<option value="breaking-news">🔴 Breaking News</option>
									</Form.Select>
									<Form.Text className="text-muted">
										Breaking news appears in a special section
									</Form.Text>
								</Form.Group>
							</Col>

							<Col md={4}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Display Options</Form.Label>
									<Form.Check
										type="checkbox"
										name="featured"
										label="⭐ Featured Article"
										checked={formData.featured}
										onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
										className="mt-2"
									/>
									<Form.Text className="text-muted d-block">
										Shows prominently at the top
									</Form.Text>
								</Form.Group>
							</Col>
						</Row>

						{/* Content Section */}
						<h5 className="mb-3 pb-2 border-bottom">✍️ Content</h5>
						<Row className="g-3 mb-4">


							<Col xs={12}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Content *</Form.Label>

									<Editor
										editorState={editorState}
										wrapperClassName="demo-wrapper"
										editorClassName={`demo-editor ${validationErrors.content ? 'is-invalid' : ''}`}
										onEditorStateChange={(state) => {
											setEditorState(state);
											if (validationErrors.content) {
												setValidationErrors((prev) => ({ ...prev, content: "" }));
											}
										}}
									/>
									{validationErrors.content && (
										<div className="text-danger small mt-1">{validationErrors.content}</div>
									)}
								</Form.Group>
							</Col>

							<Col xs={12}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Thumbnail / Banner Image</Form.Label>
									<p className="text-muted small mb-2">This image will be displayed as the article banner in the admin panel</p>
									<ImageUpload
										currentImage={formData.thumbnail}
										onImageUpload={handleImageUpload}
										user={user}
									/>
									{formData.thumbnail && (
										<div className="mt-3">
											<small className="text-muted d-block mb-2">Banner Preview:</small>
											<div style={{
												width: '100%',
												height: '200px',
												borderRadius: '8px',
												overflow: 'hidden',
												border: '2px solid #dee2e6'
											}}>
												<img
													src={formData.thumbnail}
													alt="Banner preview"
													style={{
														width: '100%',
														height: '100%',
														objectFit: 'cover'
													}}
												/>
											</div>
										</div>
									)}
								</Form.Group>
							</Col>

						</Row>

						{/* SEO Section */}
						<h5 className="mb-3 pb-2 border-bottom">🔍 SEO Settings</h5>
						<Row className="g-3 mb-4">
							<Col md={12}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Meta Title (SEO)</Form.Label>
									<Form.Control
										name="meta_title"
										value={formData.meta_title}
										onChange={handleChange}
										isInvalid={!!validationErrors.meta_title}
										placeholder="SEO optimized title"
									/>
									<Form.Control.Feedback type="invalid">
										{validationErrors.meta_title}
									</Form.Control.Feedback>
								</Form.Group>
							</Col>

							<Col md={12}>
								<Form.Group className="mb-3">
									<Form.Label className="fw-semibold">Meta Description</Form.Label>
									<Form.Control
										as="textarea"
										rows={2}
										name="meta_desc"
										value={formData.meta_desc}
										onChange={handleChange}
										isInvalid={!!validationErrors.meta_desc}
										placeholder="Brief description for search engines"
									/>
									<Form.Control.Feedback type="invalid">
										{validationErrors.meta_desc}
									</Form.Control.Feedback>
								</Form.Group>
							</Col>
						</Row>

						<div className="d-flex justify-content-end mt-4 gap-2">
							<Button variant="secondary" onClick={() => navigate("/admin-panel/articles")}>
								Cancel
							</Button>
							<Button type="submit" variant="primary" disabled={saving}>
								{saving ? "Saving..." : id ? "Update Article" : "Create Article"}
							</Button>
						</div>
					</Form>
				</Card.Body>
			</Card>
		</Container>
	);
}

const mapStateToProps = (state) => ({
	user: state.user
});

export default connect(mapStateToProps)(AdminArticleForm);
